// client/src/pages/POA/poaValidation.ts
// IRS Form 2848 validation rules, per-step.
// Returns a Record<fieldKey, errorMessage> — empty object means valid.

import type { POAFormState, POARepresentative, POATaxMatter, StepErrors } from '../../types/poa';
import { DESIGNATION_CODES, MAX_FUTURE_YEAR, MIN_YEAR, MAX_TAX_MATTERS } from './poaConstants';

const EIN_RE  = /^\d{2}-\d{7}$/;
const SSN_RE  = /^\d{3}-\d{2}-\d{4}$/;
const PTIN_RE = /^P\d{8}$/i;

// ── Step 1: Taxpayer ────────────────────────────────────────────────────────

export function validateTaxpayerStep(s: POAFormState): StepErrors {
  const errors: StepErrors = {};

  if (!s.taxpayerName.trim())
    errors.taxpayerName = 'Taxpayer name is required.';

  if (!s.taxpayerAddress.trim())
    errors.taxpayerAddress = 'Taxpayer address is required.';

  const hasEin = s.taxpayerEin.trim().length > 0;
  const hasSsn = s.taxpayerSsn.trim().length > 0;

  if (!hasEin && !hasSsn) {
    errors.taxpayerTin = 'At least one taxpayer identification number (EIN or SSN) is required.';
  } else {
    if (hasEin && !EIN_RE.test(s.taxpayerEin.trim()))
      errors.taxpayerEin = 'EIN must be in the format XX-XXXXXXX (e.g. 63-0422062).';
    if (hasSsn && !SSN_RE.test(s.taxpayerSsn.trim()))
      errors.taxpayerSsn = 'SSN must be in the format XXX-XX-XXXX (e.g. 448-58-8429).';
  }

  return errors;
}

// ── Step 2: Representatives ─────────────────────────────────────────────────

export function validateRepresentativesStep(s: POAFormState): StepErrors {
  const errors: StepErrors = {};

  if (s.representatives.length === 0) {
    errors._reps = 'At least one representative is required.';
    return errors;
  }

  if (s.representatives.length > 4)
    errors._reps = 'A maximum of 4 representatives may be listed on Form 2848.';

  const noticeCount = s.representatives.filter((r) => r.receivesNotices).length;
  if (noticeCount > 2)
    errors._notices = 'The IRS sends notices to at most 2 representatives. Please uncheck "receives notices" on some reps.';

  s.representatives.forEach((rep, i) => {
    const p = `rep_${i}`;
    if (!rep.name.trim())    errors[`${p}_name`]    = 'Representative name is required.';
    if (!rep.address.trim()) errors[`${p}_address`] = 'Representative address is required.';

    if (!rep.designationCode)
      errors[`${p}_designation`] = 'Designation code is required.';

    const desDef = DESIGNATION_CODES.find((d) => d.value === rep.designationCode);
    if (desDef?.requiresLicense) {
      if (!rep.licensingJurisdiction.trim())
        errors[`${p}_jurisdiction`] = 'Licensing jurisdiction is required for this designation.';
      if (!rep.licenseNumber.trim())
        errors[`${p}_license`] = 'License/enrollment number is required for this designation.';
    }

    if (rep.ptin.trim() && !PTIN_RE.test(rep.ptin.trim()))
      errors[`${p}_ptin`] = 'PTIN must be in the format P00000000.';
  });

  return errors;
}

// ── Step 3: Tax Matters ─────────────────────────────────────────────────────

export function validateTaxMattersStep(s: POAFormState): StepErrors {
  const errors: StepErrors = {};

  if (s.taxMatters.length === 0) {
    errors._matters = 'At least one tax matter is required.';
    return errors;
  }

  // The form itself is the constraint: Line 3 has three rows, and a fourth would be
  // dropped when the PDF is generated rather than appearing anywhere on the filing.
  if (s.taxMatters.length > MAX_TAX_MATTERS) {
    errors._matters = `Form 2848 Line 3 has room for ${MAX_TAX_MATTERS} matters. Remove one, or create a second Form 2848 for the rest.`;
    return errors;
  }

  s.taxMatters.forEach((m, i) => {
    const p = `matter_${i}`;

    if (!m.matterDescription.trim() || m.matterDescription === '— select —')
      errors[`${p}_desc`] = 'Description of matter is required.';

    if (!m.taxFormNumber.trim() || m.taxFormNumber === '__other__')
      errors[`${p}_form`] = 'Tax form number is required. Select from the list or enter a custom value below.';

    if (m.periodType === 'year') {
      if (!m.periodStart)
        errors[`${p}_period`] = 'Tax year is required.';
      else
        errors[`${p}_period`] = validateYear(Number(m.periodStart));
    } else if (m.periodType === 'range') {
      if (!m.periodStart || !m.periodEnd) {
        errors[`${p}_period`] = 'Both start and end years are required for a range.';
      } else {
        const startErr = validateYear(Number(m.periodStart));
        const endErr   = validateYear(Number(m.periodEnd));
        if (startErr) errors[`${p}_period`] = startErr;
        else if (endErr) errors[`${p}_period`] = endErr;
        else if (Number(m.periodEnd) < Number(m.periodStart))
          errors[`${p}_period`] = 'End year must be equal to or after start year.';
      }
    } else if (m.periodType === 'specific') {
      if (!m.periodRaw.trim())
        errors[`${p}_period`] = 'Period is required.';
      else if (/^(all|any)/i.test(m.periodRaw.trim()))
        errors[`${p}_period`] = 'The IRS does not accept generic period references like "All years". Please specify the exact year(s).';
    }

    // Remove the key if there's no error (empty string doesn't count)
    if (errors[`${p}_period`] === '') delete errors[`${p}_period`];
  });

  return errors;
}

function validateYear(year: number): string {
  if (isNaN(year) || year < MIN_YEAR || year > MAX_FUTURE_YEAR)
    return `Year must be between ${MIN_YEAR} and ${MAX_FUTURE_YEAR}. The IRS does not record future years beyond 3 years from December 31 of the current year.`;
  return '';
}

// ── Step 4: Acts — no required fields ──────────────────────────────────────

export function validateActsStep(_s: POAFormState): StepErrors {
  return {};
}

// ── All steps combined (for final review check) ─────────────────────────────

export function validateAll(s: POAFormState): { step: number; errors: StepErrors }[] {
  return [
    { step: 0, errors: validateTaxpayerStep(s) },
    { step: 1, errors: validateRepresentativesStep(s) },
    { step: 2, errors: validateTaxMattersStep(s) },
    { step: 3, errors: validateActsStep(s) },
  ].filter(({ errors }) => Object.keys(errors).length > 0);
}

export const STEP_VALIDATORS = [
  validateTaxpayerStep,
  validateRepresentativesStep,
  validateTaxMattersStep,
  validateActsStep,
];
